import FetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Queries/FetchWorkflowRun';
import fetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/fetchWorkflowRun';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type WorkflowRunDocumentView from '@UI/Store/WorkflowRuns/Views/WorkflowRunDocumentView';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

export type ReviewStage = {
	readonly preparationId: number;
	/** 0-indexed, as the preparation numbers its stages. */
	readonly stage: number;
	readonly preparationPath: string;
	/** `null` until the preparation is loaded, or when it cannot be read. */
	readonly preparation: WorkflowRunView | null;
	/** The documents of the stage; empty until the preparation is loaded. */
	readonly documents: readonly WorkflowRunDocumentView[];
};

const parseNonNegativeInteger = (raw: string | null): number | null => {
	if (raw === null) {
		return null;
	}

	const value = Number(raw);

	return Number.isInteger(value) && value >= 0 ? value : null;
};

/**
 * The stage a document is reviewed in, read from `?preparation=&stage=` and from the preparation the
 * screen reads: `null` when the screen was not opened from a preparation (from the list of documents,
 * say), in which case it shows and verifies the one document it is on. A preparation that cannot be
 * read leaves `documents` empty, and the screen then behaves the same way.
 */
const useReviewStage = (subject: SubjectAddress): ReviewStage | null => {
	const [searchParams] = useSearchParams();
	const preparationIdParam = parseNonNegativeInteger(searchParams.get('preparation'));
	const preparationId = preparationIdParam !== null && preparationIdParam > 0 ? preparationIdParam : null;
	const stage = parseNonNegativeInteger(searchParams.get('stage'));

	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const dispatch = useDispatch();

	const { data: detail } = useStoreQuery({
		selector: (state) => state.workflowRunsStore.workflowRun,
		stopOn: (value) => preparationId === null || (value !== null && value.id === preparationId),
		query: () => {
			if (preparationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchWorkflowRun(new FetchWorkflowRun(facilityId, subject, preparationId)));
		},
		deps: [facilityId, subject, preparationId],
	});
	const preparation = detail !== null && detail.id === preparationId ? detail : null;

	if (preparationId === null || stage === null) {
		return null;
	}

	return {
		preparationId,
		stage,
		preparationPath: `${buildSubjectPath(subject)}/preparations/${preparationId}`,
		preparation,
		documents:
			preparation?.documentStages.find((candidate) => candidate.stage === stage)?.documents ?? [],
	};
};

export default useReviewStage;
